import React, { useMemo, useRef, useState, useEffect } from "react";
import { CaretDown, MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Hierarchical, searchable region selector (country → bundesland → region).
 * Replaces the inline button-bar when more than ~6 regions are present.
 */
export default function RegionSelect({ regions, value, onChange, testid = "region-select" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = regions.find((r) => r.id === value);

  const grouped = useMemo(() => {
    const g = {};
    const query = q.trim().toLowerCase();
    regions.forEach((r) => {
      if (
        query &&
        !`${r.label} ${r.bundesland} ${r.country}`.toLowerCase().includes(query)
      )
        return;
      const c = r.country || "?";
      const b = r.bundesland || "?";
      g[c] = g[c] || {};
      g[c][b] = g[c][b] || [];
      g[c][b].push(r);
    });
    return g;
  }, [regions, q]);

  return (
    <div ref={ref} className="relative inline-block" data-testid={testid}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-3 py-2 border border-white/10 bg-[#121212] rounded-sm text-sm hover:bg-white/5 transition-colors min-w-[220px] justify-between"
        data-testid={`${testid}-trigger`}
      >
        <span className="text-left">
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block leading-none mb-0.5">
            {current?.country}
            {current?.bundesland ? ` · ${current.bundesland}` : ""}
          </span>
          <span className="font-medium text-white">
            {current?.label || "Region wählen"}
          </span>
        </span>
        <CaretDown size={14} className="text-zinc-400 shrink-0" />
      </button>
      {open && (
        <div
          className="absolute z-40 mt-1 w-[320px] max-h-[380px] overflow-y-auto eid-card shadow-lg"
          data-testid={`${testid}-menu`}
        >
          <div className="p-2 sticky top-0 bg-[#121212] border-b border-white/10">
            <div className="flex items-center gap-2 border border-white/10 rounded-sm px-2 py-1.5">
              <MagnifyingGlass size={14} className="text-zinc-500" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Suche: Tirol, Bayern, Linz…"
                data-testid={`${testid}-search`}
                className="flex-1 bg-transparent text-sm focus:outline-none"
              />
            </div>
          </div>
          <div className="py-1">
            {Object.entries(grouped).map(([c, blMap]) => (
              <div key={c}>
                <div className="px-3 pt-2 pb-1 eid-label">{c}</div>
                {Object.entries(blMap).map(([bl, list]) => (
                  <div key={bl}>
                    <div className="px-3 pt-1 text-[11px] font-medium text-zinc-400">{bl}</div>
                    {list.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => {
                          onChange(r.id);
                          setOpen(false);
                        }}
                        data-testid={`${testid}-option-${r.id}`}
                        className={`w-full text-left px-3 py-1.5 text-sm hover:bg-white/5 transition-colors ${
                          value === r.id ? "bg-[#00FA9A]/10 text-[#00FA9A]" : "text-white"
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            ))}
            {Object.keys(grouped).length === 0 && (
              <div className="p-3 text-xs text-zinc-500">Keine Treffer.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
